import { shallowRef, onMounted, onUnmounted } from "vue";

export type MediaWarningsTestType = "processing" | "selection" | "both";

export interface ContactDebugFlags {
	useScribeIframeReports?: boolean;
	pdfReportHeader?: boolean;
	luckyCheat?: boolean;
	mediaWarningsTest?: {
		enabled?: boolean;
		type?: MediaWarningsTestType;
	};
}

export interface ContactDebugController {
	useScribeIframeReports?: boolean;
	pdfReportHeader?: boolean;
	luckyCheat?: boolean;
	mediaWarningsTest?: ContactDebugFlags["mediaWarningsTest"];
	get: () => ContactDebugFlags;
	set: <K extends keyof ContactDebugFlags>(key: K, value: ContactDebugFlags[K]) => void;
	patch: (flags: Partial<ContactDebugFlags>) => void;
	reset: () => void;
}

const CONTACT_DEBUG_EVENT = "contact:debug-flags-changed";

const cloneFlags = (flags: ContactDebugFlags): ContactDebugFlags => ({
	...flags,
	mediaWarningsTest: flags.mediaWarningsTest ? { ...flags.mediaWarningsTest } : undefined,
});

const readInitialFlags = (): ContactDebugFlags => {
	const existing = window.__contactDebug;
	if (!existing) {
		return {};
	}

	return cloneFlags({
		useScribeIframeReports: existing.useScribeIframeReports,
		pdfReportHeader: existing.pdfReportHeader,
		luckyCheat: existing.luckyCheat,
		mediaWarningsTest: existing.mediaWarningsTest,
	});
};

export const installContactDebugController = (): ContactDebugController | null => {
	if (typeof window === "undefined") {
		return null;
	}

	if (window.__contactDebug?.get && window.__contactDebug?.set) {
		return window.__contactDebug;
	}

	const state = readInitialFlags();
	const dispatch = () => {
		window.dispatchEvent(new CustomEvent(CONTACT_DEBUG_EVENT, { detail: cloneFlags(state) }));
	};

	const controller: ContactDebugController = {
		get: () => cloneFlags(state),
		set: (key, value) => {
			(state as any)[key] = typeof value === "object" && value !== null ? { ...value } : value;
			dispatch();
		},
		patch: (flags) => {
			if (flags.mediaWarningsTest !== undefined) {
				state.mediaWarningsTest = flags.mediaWarningsTest ? { ...flags.mediaWarningsTest } : undefined;
			}
			if (flags.useScribeIframeReports !== undefined) {
				state.useScribeIframeReports = flags.useScribeIframeReports;
			}
			if (flags.pdfReportHeader !== undefined) {
				state.pdfReportHeader = flags.pdfReportHeader;
			}
			if (flags.luckyCheat !== undefined) {
				state.luckyCheat = flags.luckyCheat;
			}
			dispatch();
		},
		reset: () => {
			state.useScribeIframeReports = undefined;
			state.pdfReportHeader = undefined;
			state.luckyCheat = false;
			state.mediaWarningsTest = undefined;
			dispatch();
		},
	};

	Object.defineProperties(controller, {
		useScribeIframeReports: {
			get: () => state.useScribeIframeReports,
			set: (value: boolean | undefined) => {
				state.useScribeIframeReports = value;
				dispatch();
			},
			enumerable: true,
			configurable: true,
		},
		pdfReportHeader: {
			get: () => state.pdfReportHeader,
			set: (value: boolean | undefined) => {
				state.pdfReportHeader = value;
				dispatch();
			},
			enumerable: true,
			configurable: true,
		},
		luckyCheat: {
			get: () => state.luckyCheat,
			set: (value: boolean | undefined) => {
				state.luckyCheat = value;
				dispatch();
			},
			enumerable: true,
			configurable: true,
		},
		mediaWarningsTest: {
			get: () => (state.mediaWarningsTest ? { ...state.mediaWarningsTest } : undefined),
			set: (value: ContactDebugFlags["mediaWarningsTest"]) => {
				state.mediaWarningsTest = value ? { ...value } : undefined;
				dispatch();
			},
			enumerable: true,
			configurable: true,
		},
	});

	window.__contactDebug = controller;
	return controller;
};

export const getContactDebugFlags = (): ContactDebugFlags => {
	return installContactDebugController()?.get() || {};
};

export const useContactDebugFlags = () => {
	const flags = shallowRef<ContactDebugFlags>(getContactDebugFlags());
	const sync = () => {
		flags.value = getContactDebugFlags();
	};

	onMounted(() => {
		installContactDebugController();
		sync();
		window.addEventListener(CONTACT_DEBUG_EVENT, sync);
	});

	onUnmounted(() => {
		window.removeEventListener(CONTACT_DEBUG_EVENT, sync);
	});

	return flags;
};
